import axios from "axios";
import intl from "react-intl-universal";
import {
  applyBearerAuth,
  dispatchAuthUnauthorized,
  assertAuthTokenNotExpired,
} from "./authToken";
import { API_URL, CONSOLE_API_URL } from "../config";

const consoleApi = axios.create({
  baseURL: "",
});

consoleApi.interceptors.request.use(
  (config) => {
    assertAuthTokenNotExpired();
    return applyBearerAuth(config);
  },
  (error) => Promise.reject(error),
);

consoleApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      dispatchAuthUnauthorized();
    }
    return Promise.reject(error);
  },
);

/** MobileService base — IB JWT + `/v1/ib/console/*` transparent BaaS proxy + `/v1/ib/baas/*` home. */
export const getMobileServiceBaseUrl = () => {
  const base = typeof API_URL === "string" ? API_URL.trim() : "";
  return base.replace(/\/+$/, "");
};

/**
 * @deprecated Programs/instances home now uses MobileService `/v1/ib/baas/*`.
 * Kept only for optional legacy Console.Api tooling.
 */
export const getConsoleBaseUrl = () => {
  const base = typeof CONSOLE_API_URL === "string" ? CONSOLE_API_URL.trim() : "";
  return base.replace(/\/+$/, "");
};

export const createIdempotencyKey = () => {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const normalizeConsoleError = (error) => {
  const status = error?.response?.status ?? null;
  const data = error?.response?.data;
  const title =
    data?.title ||
    data?.error ||
    data?.detail ||
    data?.message ||
    error?.message ||
    "Console API request failed";
  const detail = data?.detail || data?.description || data?.message || null;
  const code = data?.error || data?.code || null;

  const normalized = new Error(detail || title);
  normalized.name = "BaasConsoleError";
  normalized.status = status;
  normalized.code = code;
  normalized.title = title;
  normalized.detail = detail;
  normalized.data = data;
  normalized.errors = data?.errors || null;
  normalized.raw = error;

  // Partner keys never carry Valut-only scopes; surface a stable code for UI copy.
  // Only remap after a real API 403/502 — never invent this client-side before the request.
  const detailText = String(detail || "");
  const errorCode = String(code || data?.error || "").toLowerCase();
  if (
    status === 403
    && /missing scopes:\s*.*(instances\.(scopes|features|limits)\.write|entities\.features\.write)/i.test(
      detailText,
    )
  ) {
    normalized.code = "valut_admin_scope_required";
  }
  if (
    errorCode === "valut_admin_key_missing"
    || errorCode === "valut_admin_required"
    || data?.error === "valut_admin_key_missing"
    || data?.error === "valut_admin_required"
  ) {
    normalized.code = errorCode === "valut_admin_required"
      ? "valut_admin_required"
      : "valut_admin_scope_required";
  }

  // Baas without POST /v1/entities/autocomplete deployed matches entity_id=autocomplete
  // and returns 405 Allow: GET, PATCH (empty body) — not a SERPRO/credentials issue.
  if (status === 405) {
    const allow = String(error?.response?.headers?.allow || "").toUpperCase();
    const missingAutocompleteRoute =
      allow.includes("GET") && allow.includes("PATCH") && !allow.includes("POST");
    normalized.code = missingAutocompleteRoute
      ? "baas_autocomplete_not_deployed"
      : "method_not_allowed";
    normalized.detail = missingAutocompleteRoute
      ? "POST /v1/entities/autocomplete ainda não está disponível neste Baas. Faça rebuild + redeploy (Azure) ou reinicie o Baas local após dotnet build."
      : detail || title || "HTTP method not allowed for this BaaS route.";
    normalized.message = normalized.detail;
  }

  // Nested BaaS 401 body used to be forwarded as detail JSON — normalize for UI.
  const nested =
    typeof detail === "string" && detail.trim().startsWith("{")
      ? (() => {
          try {
            return JSON.parse(detail);
          } catch {
            return null;
          }
        })()
      : null;
  const nestedError = String(nested?.error || "").toLowerCase();
  if (
    nestedError === "invalid_api_key"
    || errorCode === "invalid_api_key"
    || errorCode === "api_key_not_found"
    || errorCode === "baas_unauthorized"
  ) {
    normalized.code =
      nestedError === "invalid_api_key" || errorCode === "invalid_api_key"
        ? "invalid_api_key"
        : errorCode === "api_key_not_found"
          ? "api_key_not_found"
          : "baas_unauthorized";
  }

  return normalized;
};

/**
 * User-facing copy when the selected instance has no/wrong partner API key.
 * Avoid dumping raw `{"title":"Unauthorized","status":401,"error":"invalid_api_key"}` JSON.
 */
export const formatBaasInstanceCredentialError = (error, fallback = null) => {
  if (!error) return null;

  const code = String(error?.code || error?.data?.error || "").toLowerCase();
  const detail = String(error?.detail || error?.message || "").trim();
  const nestedError = (() => {
    if (!detail.startsWith("{")) return "";
    try {
      return String(JSON.parse(detail)?.error || "").toLowerCase();
    } catch {
      return "";
    }
  })();

  if (code === "api_key_not_found" || nestedError === "api_key_not_found") {
    const message =
      "Esta instância não tem API key configurada para o ambiente selecionado. "
      + "Cadastre uma chave partner (dev_ no HML / prod_ na Production) antes de abrir o console.";
    const normalized = new Error(message);
    normalized.name = "BaasConsoleError";
    normalized.code = "api_key_not_found";
    normalized.status = error?.status ?? 404;
    normalized.detail = message;
    normalized.title = message;
    normalized.data = error?.data;
    return normalized;
  }

  if (
    code === "invalid_api_key"
    || nestedError === "invalid_api_key"
    || code === "baas_unauthorized"
  ) {
    const message =
      "A API key desta instância foi rejeitada pelo BaaS (invalid_api_key). "
      + "Ao trocar de instância, a chave anterior não é reutilizada — confira se a "
      + "instância selecionada tem uma chave válida no ambiente (HML vs Production).";
    const normalized = new Error(message);
    normalized.name = "BaasConsoleError";
    normalized.code = "invalid_api_key";
    normalized.status = error?.status ?? 403;
    normalized.detail = detail && !detail.startsWith("{") ? detail : message;
    normalized.title = message;
    normalized.data = error?.data;
    return normalized;
  }

  if (fallback && !detail) {
    const normalized = new Error(fallback);
    normalized.name = "BaasConsoleError";
    normalized.code = code || null;
    normalized.status = error?.status ?? null;
    normalized.detail = fallback;
    return normalized;
  }

  return error;
};

/**
 * Prefer BaaS field-level 409 messages and Problem Details `detail` over a generic toast.
 * 409 is not only `program_code` — see formatBaasCreateConflictMessage.
 */
export const formatBaasConsoleConflictMessage = (error, fallback = null) => {
  const code = String(error?.code || error?.data?.error || error?.error || "").toLowerCase();
  if (code === "documents_contract_required") {
    return intl.get("baas.entidades.detail.lepContractRequiredHint")
      || String(error?.detail || error?.data?.detail || "").trim()
      || fallback;
  }

  const fieldErrors = error?.errors || error?.data?.errors;
  if (fieldErrors && typeof fieldErrors === "object") {
    const messages = Object.entries(fieldErrors)
      .flatMap(([field, value]) => {
        const list = Array.isArray(value) ? value : [value];
        return list
          .map((entry) => String(entry || "").trim())
          .filter(Boolean)
          .map((entry) => (entry.includes(":") ? entry : `${field}: ${entry}`));
      })
      .filter(Boolean);
    if (messages.length > 0) {
      return messages.join(" ");
    }
  }

  const detail = String(error?.detail || error?.data?.detail || error?.message || "").trim();
  if (
    /articles of incorporation|ARTICLES_OF_INCORPORATION|contrato social|documents_contract_required/i
      .test(detail)
  ) {
    return intl.get("baas.entidades.detail.lepContractRequiredHint") || detail;
  }
  if (detail) return detail;

  if (code === "duplicate_request") {
    return "A request with the same X-Idempotency-Key is already in progress or completed.";
  }

  return fallback;
};

const buildMobileUrl = (path) => {
  const base = getMobileServiceBaseUrl();
  const normalizedPath = String(path || "").replace(/^\/+/, "");
  if (!base) {
    return `/${normalizedPath}`;
  }
  return `${base}/${normalizedPath}`;
};

/**
 * Low-level MobileService request with the IB JWT session.
 * Prefer {@link baasProxy} for BaaS paths under `/v1/ib/console/{**path}`.
 * Prefer {@link getConsolePrograms} for Option B home listing under `/v1/ib/baas/*`.
 */
export const mobileConsoleRequest = async ({
  method = "GET",
  path,
  body,
  headers = {},
  query,
  signal,
} = {}) => {
  const upperMethod = String(method || "GET").toUpperCase();
  const requestHeaders = { ...headers };

  if (upperMethod === "POST" && !requestHeaders["X-Idempotency-Key"]) {
    requestHeaders["X-Idempotency-Key"] = createIdempotencyKey();
  }

  if (body != null && !requestHeaders["Content-Type"]) {
    requestHeaders["Content-Type"] = "application/json";
  }

  try {
    const response = await consoleApi.request({
      url: buildMobileUrl(path),
      method: upperMethod,
      data: body,
      headers: requestHeaders,
      params: query,
      signal,
    });
    return response.data;
  } catch (error) {
    throw normalizeConsoleError(error);
  }
};

/**
 * @deprecated Use {@link mobileConsoleRequest} or {@link baasProxy}.
 * Alias kept so older call sites compile during migration.
 */
export const consoleRequest = mobileConsoleRequest;

/**
 * Resolve numeric BaasApiInstanceId + environment from a selection object or composite id.
 * Composite id format: `{baasInstanceId}:{environment}` (e.g. `1:development`).
 */
export const resolveBaasProxyContext = (instanceOrId) => {
  if (instanceOrId == null || instanceOrId === "") {
    const err = new Error("BaaS instance is required");
    err.name = "BaasConsoleError";
    err.code = "instance_required";
    throw err;
  }

  if (typeof instanceOrId === "object") {
    const instanceId = Number(
      instanceOrId.baasInstanceId ?? instanceOrId.instanceId ?? instanceOrId.instance_id,
    );
    const environment = String(instanceOrId.environment || "")
      .trim()
      .toLowerCase();

    if (!Number.isFinite(instanceId) || instanceId <= 0 || !environment) {
      const err = new Error("Invalid BaaS instance selection for proxy routing");
      err.name = "BaasConsoleError";
      err.code = "instance_invalid";
      throw err;
    }

    return { instanceId, environment };
  }

  const raw = String(instanceOrId);
  const separator = raw.indexOf(":");
  if (separator > 0) {
    const instanceId = Number(raw.slice(0, separator));
    const environment = raw.slice(separator + 1).trim().toLowerCase();
    if (Number.isFinite(instanceId) && instanceId > 0 && environment) {
      return { instanceId, environment };
    }
  }

  const err = new Error(
    `Cannot resolve Console proxy target from instance id "${raw}". Expected {baasInstanceId, environment} or "{id}:{environment}".`,
  );
  err.name = "BaasConsoleError";
  err.code = "instance_invalid";
  throw err;
};

/**
 * Proxy a request to BaaS via MobileService:
 * `/v1/ib/console/{baasPath}` + `X-Instance-Id` + `X-Baas-Environment`.
 * Server injects partner `X-Api-key` — never send a client API key.
 *
 * HML cards (Option B `HmlInstanceId`) send the HML numeric id in `X-Instance-Id`.
 * Same-id cross-DB pointers (`HmlInstanceId == Prod id`) share one numeric id for both
 * cards — `X-Baas-Environment` disambiguates so Prod does not inherit the HML/`dev_` lane.
 * MobileService authorizes via Prod membership + `HmlInstanceId`, then proxies to the
 * same `Baas:BaseUrl` with a `dev_`/`prod_` partner key (green host routes by prefix).
 * Optional `Baas:DevBaseUrl` overrides host for all lanes when set (local Baas + prod DB).
 * Response headers `X-Baas-Proxy-Environment` / `X-Baas-Proxy-Base-Host` show the route.
 */
export const baasProxy = async ({
  instanceId,
  environment,
  instance,
  method = "GET",
  baasPath,
  body,
  query,
  headers = {},
  signal,
} = {}) => {
  const ctx =
    instance != null
      ? resolveBaasProxyContext(instance)
      : resolveBaasProxyContext({ baasInstanceId: instanceId, environment });

  const normalizedBaasPath = String(baasPath || "").replace(/^\/+/, "");
  if (!normalizedBaasPath) {
    const err = new Error("baasPath is required");
    err.name = "BaasConsoleError";
    err.code = "proxy_path_required";
    throw err;
  }

  const env =
    ctx.environment === "development" || ctx.environment === "dev"
      ? "development"
      : "production";

  // Always send HML/Prod instance id from the selected card (development → HmlInstanceId).
  // Environment is required when Prod.HmlInstanceId equals the Prod id (same-id Option B).
  return mobileConsoleRequest({
    method,
    path: `v1/ib/console/${normalizedBaasPath}`,
    body,
    query,
    headers: {
      ...headers,
      "X-Instance-Id": String(ctx.instanceId),
      "X-Baas-Environment": env,
    },
    signal,
  });
};

/**
 * Option B programs/instances home via MobileService (same IB JWT as baasProxy).
 * Does not use Valut.Console.Api — path is `/v1/ib/baas/*` (not the console catch-all).
 */
export const getConsolePrograms = (options = {}) =>
  mobileConsoleRequest({ method: "GET", path: "v1/ib/baas/programs", ...options });

export const getConsoleInstances = (options = {}) =>
  mobileConsoleRequest({ method: "GET", path: "v1/ib/baas/instances", ...options });

export const getConsoleInstance = (instanceId, options = {}) =>
  mobileConsoleRequest({
    method: "GET",
    path: `v1/ib/baas/instances/${instanceId}`,
    ...options,
  });

/** @deprecated Console.Api auth/me — unused by /baas; IB session is MobileService JWT. */
export const getConsoleAuthMe = (options = {}) => {
  const err = new Error(
    "getConsoleAuthMe is deprecated; /baas uses the MobileService IB JWT session.",
  );
  err.name = "BaasConsoleError";
  err.code = "console_auth_me_removed";
  return Promise.reject(err);
};
